import { useMemo, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Copy, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { QueturePlayer, type QuetureApiInterface } from '@quebon/shared-learning'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { contentApi } from '@/api/content'
import type { QuizDebugContext } from './TotalQuizSession'

interface VideoReviewSectionProps {
  contentId?: number
  qid?: string | null
  lessonTitle?: string
  debugContext?: QuizDebugContext
  isLoading?: boolean
}

export function VideoReviewSection({
  contentId,
  qid,
  lessonTitle,
  debugContext,
  isLoading = false,
}: VideoReviewSectionProps) {
  const { t } = useTranslation('learner')
  const [showErrorReportDialog, setShowErrorReportDialog] = useState(false)

  // Queture API adapter for QueturePlayer
  const quetureApiAdapter: QuetureApiInterface = useMemo(
    () => ({
      getQuetureInfo: async (qid: string) => {
        if (!contentId) throw new Error('contentId is required')
        const response = await contentApi.getQuetureInfo(contentId, qid)
        return {
          qid: response.quetureId,
          title: response.title,
          hlsUrl: response.hlsUrl,
          thumbnailUrl: response.thumbnailUrl,
          duration: response.duration,
        }
      },
    }),
    [contentId]
  )

  // Fetch queture data for metadata display
  const { data: quetureData, isLoading: isLoadingQueture } = useQuery({
    queryKey: ['quetureInfo', contentId, qid],
    queryFn: () => contentApi.getQuetureInfo(contentId!, qid!),
    enabled: !!contentId && !!qid,
  })

  // Format duration
  const formatDuration = useCallback((seconds: number | null | undefined) => {
    if (!seconds) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return t('learning.videoReview.duration', { mins, secs })
  }, [])

  // Generate error report text
  const getErrorReportText = useCallback(() => {
    const reportData = {
      type: 'video',
      qid: qid || 'N/A',
      lessonId: debugContext?.lessonId,
      lessonTitle: debugContext?.lessonTitle || lessonTitle,
      moduleId: debugContext?.moduleId,
      moduleName: debugContext?.moduleName,
      courseId: debugContext?.courseId,
      courseName: debugContext?.courseName,
      curriculumId: debugContext?.curriculumId,
      curriculumName: debugContext?.curriculumName,
      contentId: debugContext?.contentId,
      hlsUrl: quetureData?.hlsUrl || 'N/A',
      duration: quetureData?.duration ? formatDuration(quetureData.duration) : 'N/A',
      copiedAt: new Date().toISOString(),
    }

    const cleanData = JSON.parse(JSON.stringify(reportData, (_, v) => v ?? undefined))

    return `=== 영상 오류 리포트 ===
QID: ${qid || 'N/A'}
${debugContext?.lessonId ? `lessonId: ${debugContext.lessonId}` : ''}
${debugContext?.lessonTitle || lessonTitle ? `lessonTitle: ${debugContext?.lessonTitle || lessonTitle}` : ''}
${debugContext?.moduleName ? `moduleName: ${debugContext.moduleName}` : ''}
${debugContext?.moduleId ? `moduleId: ${debugContext.moduleId}` : ''}
${debugContext?.courseName ? `courseName: ${debugContext.courseName}` : ''}
${debugContext?.courseId ? `courseId: ${debugContext.courseId}` : ''}
${debugContext?.curriculumName ? `curriculumName: ${debugContext.curriculumName}` : ''}
${debugContext?.curriculumId ? `curriculumId: ${debugContext.curriculumId}` : ''}
${debugContext?.contentId ? `contentId: ${debugContext.contentId}` : ''}
${quetureData?.hlsUrl ? `hlsUrl: ${quetureData.hlsUrl}` : ''}
${quetureData?.duration ? `duration: ${formatDuration(quetureData.duration)}` : ''}

=== JSON (상세) ===
${JSON.stringify(cleanData, null, 2)}
`
  }, [qid, lessonTitle, debugContext, quetureData, formatDuration])

  const handleCopyAndCloseDialog = useCallback(async () => {
    const textToCopy = getErrorReportText()
    try {
      await navigator.clipboard.writeText(textToCopy)
      setShowErrorReportDialog(false)
    } catch (err) {
      console.error('Failed to copy error report:', err)
    }
  }, [getErrorReportText])

  if (isLoading || isLoadingQueture) {
    return (
      <div className="flex flex-col justify-center items-center py-20 h-full">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        <p className="mt-4 text-gray-600">{t('learning.videoReview.loadingVideo')}</p>
      </div>
    )
  }

  if (!qid) {
    return (
      <div className="flex flex-col justify-center items-center py-20 h-full">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-gray-600 mb-2">{t('learning.videoReview.noVideo')}</p>
        <p className="text-sm text-gray-400">{t('learning.videoReview.noQid')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex justify-between items-start px-6 py-4 bg-white border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900"><ContentMetadataText text={lessonTitle || t('learning.videoReview.videoReview')} /></h2>
          {import.meta.env.DEV && <p className="mt-1 text-sm text-gray-500">
            QID: {qid}
            <DebugQid qid={qid} />
          </p>}
        </div>
        <button
          onClick={() => setShowErrorReportDialog(true)}
          className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
          title={t('learning.videoReview.errorReportTitle')}
        >
          <Copy className="w-3.5 h-3.5" />
          {t('learning.videoReview.errorReport')}
        </button>
      </div>

      {/* Video Player */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl">
          <QueturePlayer
            renderMetadataMath
            qid={qid}
            title={lessonTitle}
            autoPlay={false}
            allowSkip={true}
            quetureApi={quetureApiAdapter}
          />

          {/* Video Metadata */}
          {quetureData && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">{t('learning.videoReview.videoInfo')}</h3>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                {import.meta.env.DEV && <div>
                  <dt className="text-gray-500">QID</dt>
                  <dd className="font-mono text-gray-900">{quetureData.quetureId}</dd>
                </div>}
                <div>
                  <dt className="text-gray-500">{t('learning.videoReview.videoDuration')}</dt>
                  <dd className="text-gray-900">{formatDuration(quetureData.duration)}</dd>
                </div>
                {quetureData.title && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">{t('learning.videoReview.videoTitle')}</dt>
                    <dd className="text-gray-900"><ContentMetadataText text={quetureData.title} /></dd>
                  </div>
                )}
                {quetureData.hlsUrl && (
                  <div className="col-span-2">
                    <dt className="text-gray-500">HLS URL</dt>
                    <dd className="font-mono text-xs text-gray-600 break-all">{quetureData.hlsUrl}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Error Report Dialog */}
      <Dialog open={showErrorReportDialog} onOpenChange={setShowErrorReportDialog}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('learning.videoReview.errorReportTitle')}</DialogTitle>
          </DialogHeader>
          <pre className="p-4 font-mono text-xs text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg border border-gray-200">
            {getErrorReportText()}
          </pre>
          <DialogFooter>
            <button
              onClick={handleCopyAndCloseDialog}
              className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
            >
              <Copy className="w-4 h-4" />
              {t('learning.videoReview.copyContent')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function DebugQid({ qid }: { qid: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(qid)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-mono ml-2">
      <button
        type="button"
        onClick={handleCopy}
        className="px-1.5 py-0.5 text-gray-400 bg-gray-100 rounded-l hover:bg-gray-200 hover:text-gray-600 transition-colors cursor-pointer"
        title="QID 복사"
      >
        {copied ? 'copied!' : qid}
      </button>
      <button
        type="button"
        onClick={() => window.open(`https://adm.quebon.tv/quiz?type=&category=quetureId&by=modified&findText=${qid}`, '_blank')}
        className="px-1 py-0.5 text-gray-400 bg-gray-100 rounded-r hover:bg-gray-200 hover:text-gray-600 transition-colors cursor-pointer"
        title="Admin에서 영상 보기"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.25-.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V6.31l-5.47 5.47a.75.75 0 1 1-1.06-1.06l5.47-5.47H12.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
        </svg>
      </button>
    </span>
  )
}
